/**
 * The walls on her map: the edges of the room she has been close enough to touch.
 *
 * ── Why the sheet needs them ────────────────────────────────────────────────
 *
 * Until D-238 her sheet was a wash of the cells she walked plus a few marks,
 * and nothing else. A blind test (two independent viewers, shown the room and
 * then the sheet raised in it) could not say what the wash was, where she was,
 * or how the sheet related to the room: *"nothing on the overlay resembles
 * that layout"*. A mock with the alley's walls drawn round the same wash was
 * read at once as *"a top-down sketch of the corridor"*. The walls are what
 * tie the drawing to the room.
 *
 * ── Why only the walls NEAR where she walked ───────────────────────────────
 *
 * D-137 and `mentalMap.ts` rule 2: the sheet is her memory, not a floor plan.
 * So a wall is drawn only along the stretch of it she passed within
 * `reachMetres` of, and a room she never entered is still blank paper. The
 * sheet grows its walls the way it grows its wash.
 *
 * ── Which solids count as a wall ────────────────────────────────────────────
 *
 * There is no role for it (`solidRoles.ts` has two, neither this), and the
 * hedgerow's ground sits at four heights, so a height band alone fails
 * outdoors. `isMapWall` is geometry, checked against every room's solid list
 * on 2026-09-28: floors, ceilings, slopes, lintels and the trailer body she
 * walks under are out; walls, pens, fences, hedges, bins, pallets, the truck
 * cab and the room bounds are in. The one known miss is beat 10's culvert
 * lintel, which draws as a short line across the pipe mouth.
 */

import type { MapPoint } from '@/story/ledger';

/** The parts of a `HallSolid` the map reads. Structural, so the map does not import the world. */
export interface MapSolid {
  readonly half: { readonly x: number; readonly y: number; readonly z: number };
  readonly pos: { readonly x: number; readonly y: number; readonly z: number };
  readonly pitch?: number | undefined;
  readonly yaw?: number | undefined;
}

/** Thinner than this across, a solid is drawn as one line along its length rather than as a box. */
const LINE_BELOW_METRES = 0.3;

/**
 * Whether a solid is something she would draw as a wall.
 *
 * Unpitched (a pitched box is ground), at least 0.3 m tall (floors, decks and
 * kerb-height slabs are not), bottom below 0.45 m (she cannot walk under it,
 * which drops lintels, desk tops and the trailer body), and either thin, taller
 * than it is wide, or at least a metre high. The last clause is what keeps a
 * truck cab and drops the ditch bed, which is a wide slab whose top is a metre
 * below the yard.
 */
export function isMapWall(solid: MapSolid): boolean {
  if (solid.pitch !== undefined && solid.pitch !== 0) return false;
  const bottom = solid.pos.y - solid.half.y;
  const top = solid.pos.y + solid.half.y;
  const height = top - bottom;
  const thinnest = 2 * Math.min(solid.half.x, solid.half.z);
  if (height < 0.3 || bottom >= 0.45) return false;
  return thinnest <= 1 || height > thinnest || top >= 1;
}

/** A point on the solid's footprint, local (u along x, v along z) to world, yawed like `THREE.Euler(0, yaw, 0)`. */
function footprintPoint(solid: MapSolid, u: number, v: number): MapPoint {
  const yaw = solid.yaw ?? 0;
  const cos = Math.cos(yaw);
  const sin = Math.sin(yaw);
  return { x: solid.pos.x + u * cos + v * sin, z: solid.pos.z - u * sin + v * cos };
}

/** The lines a solid's footprint is drawn as: its centreline when thin, else its four sides. */
function outline(solid: MapSolid): (readonly [MapPoint, MapPoint])[] {
  const { x: hx, z: hz } = solid.half;
  if (2 * hx <= LINE_BELOW_METRES && hx <= hz) return [[footprintPoint(solid, 0, -hz), footprintPoint(solid, 0, hz)]];
  if (2 * hz <= LINE_BELOW_METRES) return [[footprintPoint(solid, -hx, 0), footprintPoint(solid, hx, 0)]];
  const a = footprintPoint(solid, -hx, -hz);
  const b = footprintPoint(solid, hx, -hz);
  const c = footprintPoint(solid, hx, hz);
  const d = footprintPoint(solid, -hx, hz);
  return [[a, b], [b, c], [c, d], [d, a]];
}

/**
 * The stretches of wall she walked near, as straight two-point strokes, world metres.
 *
 * Each line is sampled every `stepMetres`; a sample is kept when a walked
 * cell's centre is within `reachMetres` of it, and consecutive kept samples
 * become one stroke. Computed per raise, never per frame.
 */
export function wallStrokes(
  solids: readonly MapSolid[],
  wash: readonly MapPoint[],
  cellMetres: number,
  reachMetres = 3,
  stepMetres = 0.5,
): MapPoint[][] {
  if (wash.length === 0 || cellMetres <= 0) return [];
  const walked = new Set(wash.map((cell) => `${String(cell.x)},${String(cell.z)}`));
  const radius = Math.ceil(reachMetres / cellMetres);
  const near = (point: MapPoint): boolean => {
    const cx = Math.floor(point.x / cellMetres);
    const cz = Math.floor(point.z / cellMetres);
    for (let dx = -radius; dx <= radius; dx += 1) {
      for (let dz = -radius; dz <= radius; dz += 1) {
        if (!walked.has(`${String(cx + dx)},${String(cz + dz)}`)) continue;
        const ox = (cx + dx + 0.5) * cellMetres - point.x;
        const oz = (cz + dz + 0.5) * cellMetres - point.z;
        if (ox * ox + oz * oz <= reachMetres * reachMetres) return true;
      }
    }
    return false;
  };

  const strokes: MapPoint[][] = [];
  for (const solid of solids) {
    if (!isMapWall(solid)) continue;
    for (const [from, to] of outline(solid)) {
      const length = Math.hypot(to.x - from.x, to.z - from.z);
      const steps = Math.max(1, Math.ceil(length / stepMetres));
      let runStart: MapPoint | null = null;
      let runEnd: MapPoint | null = null;
      for (let step = 0; step <= steps; step += 1) {
        const t = step / steps;
        const point = { x: from.x + (to.x - from.x) * t, z: from.z + (to.z - from.z) * t };
        if (near(point)) {
          runStart ??= point;
          runEnd = point;
        } else if (runStart !== null && runEnd !== null) {
          if (runStart !== runEnd) strokes.push([runStart, runEnd]);
          runStart = null;
          runEnd = null;
        }
      }
      if (runStart !== null && runEnd !== null && runStart !== runEnd) strokes.push([runStart, runEnd]);
    }
  }
  return strokes;
}
